/* eslint-disable sonarjs/no-duplicate-string */
import {
  ErrorHandling,
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  FatalErrors,
  Logger,
} from "@classdojo/monsterverse-utils";
import { translate } from "@monsterverse/i18n";
import { getOrCreateRootDiv, Modal } from "@monsterverse/ui";
import { StyledEngineProvider } from "@mui/material";
import { ComponentChildren, Fragment, render } from "preact";

import { setupTranslations } from "../AWSHostURLs";
import { postToApp } from "../MessageHandler";
import { PCAppEventHandler } from "../PCAppEventHandler";

type ErrorModalProps = {
  title?: string;
  description?: string | JSX.Element[];
  yesText?: string;
  onYesClicked?: () => void;
  noText?: string;
  onNoClicked?: () => void;
  children?: ComponentChildren;
};

const FatalErrorModal: React.FC<{ props: ErrorModalProps }> = ({ props }) => {
  return (
    <Modal
      shown={true}
      titleText={props.title ?? translate("common.errors.defaults.title")}
      descriptionText={props.description}
      onYes={
        props.onYesClicked
          ? { text: props.yesText ?? translate("common.errors.defaults.ok"), onClicked: props.onYesClicked }
          : undefined
      }
      onNo={
        props.onNoClicked
          ? { text: props.noText ?? translate("common.errors.defaults.cancel"), onClicked: props.onNoClicked }
          : undefined
      }
      children={props.children}
    />
  );
};

let fatalErrorTriggered = false;
function renderFatalErrorModal(errorProps: ErrorModalProps) {
  if (fatalErrorTriggered) {
    return;
  }
  fatalErrorTriggered = true;

  render(
    <StyledEngineProvider injectFirst>
      <FatalErrorModal props={errorProps} />
    </StyledEngineProvider>,
    getOrCreateRootDiv()
  );
}

function webGLFatalError() {
  Logger.logInfo("FatalErrorModal webGLFatalError");

  const errorDescription = () => {
    return (
      <>
        <Fragment>
          {translate("common.errors.webgl_support.description")}
          <br />
          <a href="http://get.webgl.org">{translate("common.errors.webgl_support.link_text")}</a>
        </Fragment>
      </>
    );
  };
  renderFatalErrorModal({ description: [errorDescription()] });
}

function loggedOutUserFatalError() {
  Logger.logInfo("FatalErrorModal loggedOutUserFatalError");

  renderFatalErrorModal({
    title: translate("common.errors.not_logged_in.title"),
    description: translate("common.errors.not_logged_in.description"),
    yesText: translate("common.errors.not_logged_in.yes"),
    onYesClicked: () => {
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

export function getLauncherLatestURL() {
  const env = window._ENV || "local";
  if (env === "local" && !window._NGROK_HOST) {
    return undefined;
  }

  const queryParams = window.location.search ?? "";
  const match = new RegExp(/(.*\/launcher\/.*?)\/.*/g).exec(window.location.toString());
  if (match && match.length > 1) {
    return `${match[1]}/latest${queryParams}`;
  }
  return undefined;
}

function disconnectedFromServerError(error: FatalErrors) {
  Logger.logInfo(`FatalErrorModal disconnectedFromServerError ${FatalErrors[error]}`);

  const idleLabels = {
    title: translate("common.errors.idle.title"),
    yesText: translate("common.errors.idle.yes"),
    noText: translate("common.errors.idle.no"),
  };
  const otherLabels = {
    title: translate("common.errors.other.title"),
    yesText: translate("common.errors.other.yes"),
    noText: translate("common.errors.other.no"),
  };
  const labels = error === FatalErrors.ServerIdleTimeout ? idleLabels : otherLabels;
  renderFatalErrorModal({
    ...labels,
    onYesClicked: () => {
      const url = getLauncherLatestURL();
      if (url) {
        window.location.replace(url);
      } else {
        window.location.reload();
      }
    },
    onNoClicked: () => {
      console.log("return to app");
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function incompatibleBrowserError() {
  Logger.logInfo("FatalErrorModal incompatibleBrowserError");

  renderFatalErrorModal({
    title: translate("common.errors.incompatible_browser.title"),
    description: translate("common.errors.incompatible_browser.description"),
    noText: translate("common.errors.incompatible_browser.no"),
    onNoClicked: () => {
      console.log("return to app");
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function outageError() {
  Logger.logInfo("FatalErrorModal outage");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.outage.title"),
      noText: translate("common.errors.outage.no"),
      onNoClicked: () => {
        console.log("return to app");
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "200px" }}
          />
          <div style={{ fontSize: "20px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.outage.description")}
          </div>
        </div>
      ),
    });
  });
}

function assertImpossible(_p: never): never {
  throw new Error("INCONCEIVABLE!");
}

export function fatalErrorHandler(
  iframeEl: HTMLIFrameElement,
  pcAppEventHandler: PCAppEventHandler,
  error: FatalErrors
) {
  iframeEl.remove();
  pcAppEventHandler.post({ eventName: EVENT_APP_LOADING_COMPLETE });

  switch (error) {
    case FatalErrors.UserLoggedOut:
      loggedOutUserFatalError();
      break;
    case FatalErrors.NoWebGL:
      webGLFatalError();
      break;
    case FatalErrors.IncompatibleBrowser:
      incompatibleBrowserError();
      break;
    case FatalErrors.ServerIdleTimeout:
    case FatalErrors.ServerReconnectTimeout:
    case FatalErrors.ServerShuttingDown:
    case FatalErrors.WebsocketClosure:
      disconnectedFromServerError(error);
      break;
    case FatalErrors.Outage:
      outageError();
      break;
    default:
      assertImpossible(error);
  }
}

export function handleFatalErrors(iframeEl: HTMLIFrameElement, pcAppEventHandler: PCAppEventHandler) {
  pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, (data: unknown) => {
    fatalErrorHandler(iframeEl, pcAppEventHandler, data as FatalErrors);
  });

  const fatalError = (error: FatalErrors) => {
    pcAppEventHandler.post({ eventName: EVENT_APP_FATAL_ERROR, eventValues: [error] });
  };
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.UserLoggedOut, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.IncompatibleBrowser, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerIdleTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerReconnectTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerShuttingDown, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebsocketClosure, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.Outage, fatalError);
}
